<template>
  <Page subpage :title="title">
    <template #actions>
      <sera-button
        v-if="!hideForm"
        class="mr-2"
        data-cy="add-note"
        label="Add Note"
        icon="plus"
        iconRight
        @click="toggleAddModal"
      />
    </template>
    <div class="comments">
      <div
        class="d-flex align-items-end justify-content-between mb-4 flex-wrap"
      >
        <div class="d-flex align-items-center flex-wrap gap-3 mb-xs-2">
          <div class="sort-cont" @click="toggleSortingOrder">
            Sort Notes
            <i
              class="fad fa-sort-alt sorting-icon"
              :class="{ 'asc-icon': sort === 'asc' }"
            ></i>
          </div>
        </div>
        <div class="d-flex align-items-center flex-wrap mb-xs-2 gap-3">
          <ToggleSwitch v-model="filter" label-on="All" label-off="Standard" />
          <TextField
            v-model="search"
            class="mb-0"
            placeholder="Search Notes"
            search
            no-label
          />
        </div>
      </div>
      <div v-if="loading" class="loading">
        <Spinner class="mt-3" message="Loading Notes..." />
      </div>
      <div v-else-if="commentData" class="comments-list full-height-comments">
        <CommentGroup
          v-for="day in Object.keys(sortedCommentData)"
          :key="day"
          :date="day"
          :commentable-type="commentableType"
          :commentable-id="commentableId"
          :comments="commentData[day]"
          :allow-edit="allowEdit"
          @delete-comment="toggleDeleteModal"
          @updated-comment="handleUpdatedComment"
          @toggle-visibility="handleVisible"
          @toggle-important="handlePinned"
        />
      </div>
      <PaginationControls
        v-if="commentsPager"
        :pager="commentsPager"
        :no-page-length="noPageLength"
        @update:data="updateData"
      />

      <CommentFormModal
        v-if="addModal"
        :commentable-type="commentableType"
        :commentable-id="commentableId"
        @hide-modal="toggleAddModal"
        @created="handleAdd"
      />

      <Modal
        v-if="deleteId"
        title="Delete Note"
        no-submit
        @hide-modal="toggleDeleteModal"
        @delete="handleDelete"
      >
        <div>
          This note will be permanently deleted and you will not be able to undo
          this action. Are you sure?
        </div>
      </Modal>
    </div>
  </Page>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import API from "serviceshift-ui/api-client";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus";
import Pagination from "@/lib/pagination";

import Modal from "@/components/Modal.vue";
import Page from "@/components/Page.vue";
import PaginationControls from "@/components/PaginationControls.vue";
import Spinner from "@/components/Spinner2.vue";
import TextField from "@/components/TextField.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import { Comment } from "@/typings/comment";
import { Customer } from "@/typings/customer";
import CommentFormModal from "./CommentFormModal.vue";
import CommentGroup from "./CommentGroup.vue";

const props = withDefaults(
  defineProps<{
    commentableId: number;
    commentableType: string; // Customer | Job
    hideForm?: boolean;
    allowEdit?: boolean;
    title?: string;
    all?: boolean;
    noPageLength?: boolean;
  }>(),
  {
    title: "Notes",
    allowEdit: true
  }
);
const emit = defineEmits(["refresh-job"]);

const store = useStore();
const commentsPager = ref<Pagination | null>(null);
const commentData = ref<{ [k: string]: Comment[] }>({});
const search = ref("");
const filter = ref(props.all);
const addModal = ref(false);
const deleteId = ref(null);
const sort = ref("desc");
const loading = ref(false);

onMounted(() => {
  commentsPager.value = new Pagination({
    baseURL: "/admin/v4/comments/by_date",
    filteredBy: [
      { key: "commentable_id", value: props.commentableId },
      { key: "commentable_type", value: props.commentableType },
      { key: "all", value: filter.value }
    ],
    sortedBy: [{ key: "created_at", direction: "desc" }]
  });
  fetchData();
  EventBus.$on("refresh-comments", fetchData);
});

onBeforeUnmount(() => {
  EventBus.$off("refresh-comments", fetchData);
});

const customer = computed(() => {
  return store.state.currentUser as Customer | null;
});

const commentableSearchConfig = computed(() => {
  if (props.commentableId && props.commentableType) {
    return {
      commentableId: props.commentableId,
      commentableType: props.commentableType
    };
  }
  return {
    commentableId: customer.value?.id,
    commentableType: "User"
  };
});

const sortedCommentData = computed(() => {
  return Object.keys(commentData.value)
    .sort((a, b) => {
      return sort.value === "asc" ? a.localeCompare(b) : b.localeCompare(a);
    })
    .reduce((obj, key) => {
      obj[key] = commentData.value[key];
      return obj;
    }, {});
});

const fetchData = async () => {
  loading.value = true;
  try {
    const res = await commentsPager.value?.getData();
    updateData(res?.data.data);
  } catch (e) {
    // Do nothing
  }
  loading.value = false;
};

const updateData = (data: typeof commentData) => {
  commentData.value = data as any;
};

function handleAdd() {
  fetchData();
  addModal.value = false;
}

const toggleDeleteModal = (id) => {
  deleteId.value = id;
};

const handleDelete = async () => {
  await API.makeRequest(`/v1/comments/${deleteId.value}`, {
    method: "DELETE"
  });
  fetchData();
  toggleDeleteModal(null);
  emit("refresh-job");
};

const handleUpdatedComment = async () => {
  fetchData();
  emit("refresh-job");
};

const handleVisible = async (id, visible) => {
  await API.makeRequest(`/v1/comments/${id}`, {
    method: "PUT",
    data: { comment: { customer_visible: visible } }
  });
  fetchData();
  emit("refresh-job");
};

const handlePinned = async (id, pinned) => {
  await API.makeRequest(`/v1/comments/${id}`, {
    method: "PUT",
    data: { comment: { pinned } }
  });
  fetchData();
  emit("refresh-job");
};

const handleSearch = debounce(async (val) => {
  if (commentsPager.value) {
    const res = await commentsPager.value.search(val);
    updateData(res.data.data);
  }
}, 250);

const toggleAddModal = () => {
  addModal.value = !addModal.value;
};

const toggleSortingOrder = () => {
  sort.value = sort.value === "desc" ? "asc" : "desc";
};

watch(search, async (val) => {
  handleSearch(val);
});

watch(filter, async (val) => {
  if (commentsPager.value) {
    commentsPager.value.filter({
      key: "all",
      value: val ? val.toString() : null
    });
    fetchData();
  }
});

watch(commentableSearchConfig, (config) => {
  if (!commentsPager.value) return;
  commentsPager.value.filter({
    key: "commentable_id",
    value: config.commentableId ? config.commentableId.toString() : null
  });
  commentsPager.value.filter({
    key: "commentable_type",
    value: config.commentableType ? config.commentableType.toString() : null
  });
  fetchData();
});
</script>

<style scoped>
.sorting-icon {
  font-size: 14px;
  margin-right: 5px;
  cursor: pointer;
  color: var(--primary-color);
}
.loading {
  min-height: 150px;
}
.asc-icon {
  -webkit-transform: rotate(180deg);
  -moz-transform: rotate(180deg);
  -ms-transform: rotate(180deg);
  -o-transform: rotate(180deg);
  transform: rotate(180deg);
  padding-top: 3px;
}
.sort-cont {
  cursor: pointer;
}
.page-limit {
  width: 150px;
}

@media screen and (max-width: 866px) {
  .mb-xs-2 {
    margin-bottom: 15px !important;
  }
}
</style>
